import { Autocomplete, Avatar, TextField, useMediaQuery } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import Label from "../../Common/Label";
import ValidationSchema from "../Validations/Form3";
import { Form, Formik } from "formik";
import { getRequest } from "../../../api/apiServices";
import apiList from "../../../api/apiList";
import { ApiContext } from "../../../context/VonqApiContext";

interface Form3Props {
  nextStep: () => void;
  prevStep: () => void;
}

interface Education {
  id: number;
  name: string;
}

interface Seniority {
  id: number;
  name: string;
}

interface JobFunction {
  id: number;
  name: string;
}

interface Industry {
  id: number;
  name: string;
}

interface FormValues {
  education: string;
  seniority: string;
  jobFunction: string;
  industry: string;
}

const Form3: React.FC<Form3Props> = ({ nextStep, prevStep }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const apiContext = useContext(ApiContext);

  // State for all dropdown options
  const [education, setEducation] = useState<Education[]>([]);
  const [seniority, setSeniority] = useState<Seniority[]>([]);
  const [jobFunctions, setJobFunctions] = useState<JobFunction[]>([]);
  const [industries, setIndustries] = useState<Industry[]>([]);

  // Loading states
  const [isLoadingEducation, setIsLoadingEducation] = useState(false);
  const [isLoadingSeniority, setIsLoadingSeniority] = useState(false);
  const [isLoadingJobFunctions, setIsLoadingJobFunctions] = useState(false);
  const [isLoadingIndustries, setIsLoadingIndustries] = useState(false);

  // Error state
  const [error, setError] = useState<string>("");

  // Initial form values from localStorage
  const [initialValues, setInitialValues] = useState<FormValues>({
    education: "",
    seniority: "",
    jobFunction: "",
    industry: "",
  });

  const shouldFetchJobFunctions =
    !jobFunctions.length && !!initialValues.jobFunction?.trim();
  const shouldFetchIndustries =
    !industries.length && !!initialValues.industry?.trim();

  const stored = localStorage.getItem("CompaignData");
  const parsed: any =
    stored && stored !== "undefined" && stored !== "null"
      ? JSON.parse(stored)
      : {};
  const form3: any = parsed?.Form3Data || {};
  const form1: any = parsed?.Form1Data || {};
  const context: any = (apiContext as any)?.selectedValues || {};

  // Fetch Job Functions only when dropdown is opened
  const fetchJobFunctions = async () => {
    if (jobFunctions.length > 0) return; // Already loaded

    setIsLoadingJobFunctions(true);
    try {
      const response = await getRequest(apiList.getJobFunction);
      if (response && response.data && response.data.success) {
        setJobFunctions(response.data.data || []);
      } else {
        setJobFunctions([]);
      }
    } catch (error) {
      setError("Failed to fetch job function options. Please try again later.");
      setJobFunctions([]);
    } finally {
      setIsLoadingJobFunctions(false);
    }
  };

  // Fetch Industries only when dropdown is opened
  const fetchIndustries = async () => {
    if (industries.length > 0) return; // Already loaded

    setIsLoadingIndustries(true);
    try {
      const response = await getRequest(apiList.getIndustries);
      if (response && response.data && response.data.success) {
        setIndustries(response.data.data || []);
      } else {
        setIndustries([]);
      }
    } catch (error) {
      setError("Failed to fetch industry options. Please try again later.");
      setIndustries([]);
    } finally {
      setIsLoadingIndustries(false);
    }
  };

  const fetchSeniority = async () => {
    setIsLoadingSeniority(true);
    try {
      const response = await getRequest(apiList.getSeniority);
      if (response && response.data && response.data.success) {
        setSeniority(response.data.data || []);
      } else {
        setSeniority([]);
      }
    } catch (error) {
      setError("Failed to fetch seniority options. Please try again later.");
      setSeniority([]);
    } finally {
      setIsLoadingSeniority(false);
    }
  };

  const fetchEducation = async () => {
    setIsLoadingEducation(true);
    try {
      const response = await getRequest(apiList.getEducation);
      if (response && response.data && response.data.success) {
        setEducation(response.data.data || []);
      } else {
        setEducation([]);
      }
    } catch (error) {
      setError("Failed to fetch education options. Please try again later.");
      setEducation([]);
    } finally {
      setIsLoadingEducation(false);
    }
  };

  // Store form data to localStorage in existing CompaignData
  const storeFormDataToLocalStorage = (values: FormValues) => {
    // Find the complete objects from the arrays based on the selected names
    const selectedEducation = education.find(
      (edu) => edu.name === values.education
    );
    const selectedSeniority = seniority.find(
      (sen) => sen.name === values.seniority
    );
    const selectedJobFunction = jobFunctions.find(
      (func) => func.name === values.jobFunction
    );
    const selectedIndustry = industries.find(
      (ind) => ind.name === values.industry
    );

    const Form3Data = {
      education: selectedEducation || null,
      seniority: selectedSeniority || null,
      jobFunction: selectedJobFunction || null,
      industry: selectedIndustry || null,
    };

    // Get existing campaign data and update it with target group data
    const existingCampaignData = localStorage.getItem("CompaignData");
    let updatedCampaignData = {};

    if (existingCampaignData) {
      try {
        updatedCampaignData = JSON.parse(existingCampaignData);
      } catch (error) {
        setError("Error parsing existing campaign data");
      }
    }

    // Update the campaign data with target group information
    updatedCampaignData = {
      ...updatedCampaignData,
      Form3Data: Form3Data,
    };

    localStorage.setItem("CompaignData", JSON.stringify(updatedCampaignData));
  };

  // Fetch Seniority and education options
  useEffect(() => {
    fetchSeniority();
    fetchEducation();

    if (form3?.education?.id && form3?.education?.name) {
      setEducation((prev) => {
        const exists = prev.some((item) => item.id === form3.education.id);
        return exists ? prev : [form3.education, ...prev];
      });
    }
    if (form3?.seniority?.id && form3?.seniority?.name) {
      setSeniority((prev) => {
        const exists = prev.some((item) => item.id === form3.seniority.id);
        return exists ? prev : [form3.seniority, ...prev];
      });
    }
    if (form3?.jobFunction?.id && form3?.jobFunction?.name) {
      setJobFunctions((prev) => {
        const exists = prev.some((item) => item.id === form3.jobFunction.id);
        return exists ? prev : [form3.jobFunction, ...prev];
      });
    }
    if (form3?.industry?.id && form3?.industry?.name) {
      setIndustries((prev) => {
        const exists = prev.some((item) => item.id === form3.industry.id);
        return exists ? prev : [form3.industry, ...prev];
      });
    }
  }, []);

  // Prefill initial values from localStorage and context

  useEffect(() => {
    try {
      setInitialValues({
        education: form3?.education?.name || context?.education || "",
        seniority: form3?.seniority?.name || context?.seniority || "",
        jobFunction:
          form3?.jobFunction?.name ||
          form1?.jobFunction?.name ||
          context?.jobFunction ||
          "",
        industry:
          form3?.industry?.name ||
          form1?.industries?.name ||
          (Array.isArray(context?.industries)
            ? context.industries?.[0]
            : context?.industries) ||
          "",
      });
    } catch (error) {
      setError("Error while prefilling initial values" + error);
    }
  }, [apiContext]);

  // Ensure Job Functions and Industries are fetched if we already have initial values (when navigating back)
  useEffect(() => {
    if (shouldFetchJobFunctions) fetchJobFunctions();
    if (shouldFetchIndustries) fetchIndustries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues.jobFunction, initialValues.industry]);

  return (
    <div className="container" id="campaign-container">
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={(values) => {
          storeFormDataToLocalStorage(values);
          nextStep();
        }}
      >
        {({ values, errors, setFieldValue, touched, handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <div className="row flex-column gap-3">
              <div className="col d-flex align-items-start justify-content-center flex-column gap-1">
                <div className="d-flex">
                  <Avatar className="avatar">3</Avatar>
                  <Label
                    children="Target Group"
                    fontSize={"20.94px"}
                    fontWeight={700}
                    color="#273848"
                  />
                </div>
                <Label
                  children="Fill in details for your target group."
                  fontWeight={500}
                  fontSize={"12.94px"}
                  color="#273848"
                />
              </div>

              {/* Error Display */}
              {error && (
                <div className="col">
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                </div>
              )}

              {/* Education */}
              <div className="col">
                <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Education"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                    className="mb-1"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                <div>
                  <Autocomplete
                    options={education}
                    value={
                      education.find((edu) => edu.name === values.education) ||
                      null
                    }
                    onChange={(_, selectedEducation) => {
                      setFieldValue(
                        "education",
                        selectedEducation ? selectedEducation.name : ""
                      );
                    }}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select an education level"
                        variant="outlined"
                        size="small"
                        name="education"
                        error={Boolean(errors.education && touched.education)}
                        helperText={touched.education && errors.education}
                        fullWidth
                      />
                    )}
                    noOptionsText={
                      isLoadingEducation
                        ? "Loading..."
                        : "No education options found"
                    }
                    loading={isLoadingEducation}
                  />
                </div>
              </div>

              {/* Seniority */}
              <div className="col">
                <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Seniority"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                    className="mb-1"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                <div>
                  <Autocomplete
                    options={seniority}
                    value={
                      seniority.find((sen) => sen.name === values.seniority) ||
                      null
                    }
                    onChange={(_, selectedSeniority) => {
                      setFieldValue(
                        "seniority",
                        selectedSeniority ? selectedSeniority.name : ""
                      );
                    }}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select a seniority level"
                        variant="outlined"
                        size="small"
                        name="seniority"
                        error={Boolean(errors.seniority && touched.seniority)}
                        helperText={touched.seniority && errors.seniority}
                        fullWidth
                      />
                    )}
                    noOptionsText={
                      isLoadingSeniority
                        ? "Loading..."
                        : "No seniority options found"
                    }
                    loading={isLoadingSeniority}
                  />
                </div>
              </div>

              {/* Job Function */}
              <div className="col">
              <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Job Function"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                    className="mb-1"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                <div>
                  <Label
                    children="Please fill in the function of the job"
                    fontWeight={500}
                    fontSize={"14px"}
                    color="#4E4E4E"
                    className="mb-1"
                  />
                  <Autocomplete
                    options={jobFunctions}
                    value={
                      jobFunctions.find(
                        (func) => func.name === values.jobFunction
                      ) || null
                    }
                    onChange={(_, selectedJobFunction) => {
                      setFieldValue(
                        "jobFunction",
                        selectedJobFunction ? selectedJobFunction.name : ""
                      );
                    }}
                    onOpen={fetchJobFunctions} // Fetch data when dropdown opens
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select a job function"
                        variant="outlined"
                        size="small"
                        name="jobFunction"
                        error={Boolean(
                          errors.jobFunction && touched.jobFunction
                        )}
                        helperText={touched.jobFunction && errors.jobFunction}
                        fullWidth
                      />
                    )}
                    noOptionsText={
                      isLoadingJobFunctions
                        ? "Loading..."
                        : "No job function options found"
                    }
                    loading={isLoadingJobFunctions}
                  />
                </div>
              </div>

              {/* Industry */}
              <div className="col">
              <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Indutry"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                    className="mb-1"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                <div>
                  <Label
                    children="Please fill in the industry of the job"
                    fontWeight={500}
                    fontSize={"14px"}
                    color="#4E4E4E"
                    className="mb-1"
                  />
                  <Autocomplete
                    options={industries}
                    value={
                      industries.find((ind) => ind.name === values.industry) ||
                      null
                    }
                    onChange={(_, selectedIndustry) => {
                      setFieldValue(
                        "industry",
                        selectedIndustry ? selectedIndustry.name : ""
                      );
                    }}
                    onOpen={fetchIndustries} // Fetch data when dropdown opens
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select an industry"
                        variant="outlined"
                        size="small"
                        name="industry"
                        error={Boolean(errors.industry && touched.industry)}
                        helperText={touched.industry && errors.industry}
                        fullWidth
                      />
                    )}
                    noOptionsText={
                      isLoadingIndustries
                        ? "Loading..."
                        : "No industry options found"
                    }
                    loading={isLoadingIndustries}
                  />
                </div>
              </div>

              <hr className="mt-5" />
              {/* Button Section */}
              <div className="col d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 pt-4">
                <button
                  type="button"
                  className="previous-btn w-100"
                  onClick={prevStep}
                >
                  {isMobile
                    ? "Previous"
                    : "Previous - Media mix recommendation"}
                </button>
                <button className="continue-btn w-100" type="submit">
                  {isMobile ? "Next" : "Next - Recruiter info & Contact info"}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Form3;
